import React from 'react'
import { useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { motion, type Variants } from 'framer-motion'
import { Envelope, ArrowRight, ArrowLeft, CheckCircle, ArrowClockwise } from '@phosphor-icons/react'
import { toast } from 'sonner'
import { DotLoader } from '@/components/ui/DotLoader'
import { authApi } from '@/lib/api'
import {
  authInputClass as inputClass,
  authInputStyle as inputStyle,
  errorBorderClass,
} from '@/lib/styleKit'

const forgotSchema = z.object({
  email: z.string().email('Please enter a valid email address'),
})

type ForgotFormData = z.infer<typeof forgotSchema>

const easeOutExpo = [0.16, 1, 0.3, 1] as const

const formStageVariants: Variants = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { staggerChildren: 0.06, delayChildren: 0.1 },
  },
}

const formItemVariants: Variants = {
  hidden: { opacity: 0, y: 10 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5, ease: easeOutExpo } },
}

const shakeVariants: Variants = {
  idle: { x: 0 },
  error: {
    x: [0, -6, 6, -4, 4, 0],
    transition: { duration: 0.4, ease: 'easeInOut' },
  },
}

/** Seconds the "Send again" action stays locked after each dispatch. */
const RESEND_COOLDOWN_SECONDS = 30

const ForgotPasswordPage: React.FC = () => {
  const navigate = useNavigate()
  const [submitted, setSubmitted] = React.useState(false)
  const [isLoading, setIsLoading] = React.useState(false)
  // The address we actually sent to, kept so the confirmation screen can offer a
  // real re-send without asking the user to retype it.
  const [sentTo, setSentTo] = React.useState('')
  const [isResending, setIsResending] = React.useState(false)
  const [cooldown, setCooldown] = React.useState(0)

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ForgotFormData>({
    resolver: zodResolver(forgotSchema),
  })

  // Simple client-side throttle so "Send again" can't be hammered.
  React.useEffect(() => {
    if (cooldown <= 0) return
    const id = window.setTimeout(() => setCooldown((s) => s - 1), 1000)
    return () => window.clearTimeout(id)
  }, [cooldown])

  const onSubmit = async (data: ForgotFormData) => {
    setIsLoading(true)
    try {
      // The backend always returns a generic success (no account enumeration),
      // so we simply confirm the request was received.
      await authApi.forgotPassword({ email: data.email })
      setSentTo(data.email)
      setCooldown(RESEND_COOLDOWN_SECONDS)
      setSubmitted(true)
    } catch {
      toast.error('Something went wrong. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  const handleResend = async () => {
    if (!sentTo || cooldown > 0 || isResending) return
    setIsResending(true)
    try {
      await authApi.forgotPassword({ email: sentTo })
      setCooldown(RESEND_COOLDOWN_SECONDS)
      // Same non-committal wording as the panel — never confirm the account exists.
      toast.success('If an account exists for that email, another link is on its way.')
    } catch {
      toast.error('Something went wrong. Please try again.')
    } finally {
      setIsResending(false)
    }
  }

  return (
    <motion.div
      className="w-full"
      variants={formStageVariants}
      initial="hidden"
      animate="show"
    >
      {/* Header */}
      <motion.div className="mb-8" variants={formItemVariants}>
        <h1
          className="text-[#0f1011] dark:text-[#f7f8f8]"
          style={{
            fontFamily: "'Instrument Serif', 'Iowan Old Style', 'Apple Garamond', Georgia, serif",
            fontSize: '36px',
            lineHeight: 1.05,
            letterSpacing: '-0.8px',
            fontWeight: 400,
          }}
        >
          Reset your <span className="italic text-[#5e6ad2] dark:text-[#7170ff]">password</span>
        </h1>
        <p
          className="text-[#5f6166] dark:text-[#8a8f98] mt-2"
          style={{
            fontSize: '15px',
            lineHeight: 1.5,
            letterSpacing: '-0.165px',
            fontWeight: 400,
          }}
        >
          {submitted
            ? 'Check your inbox for the next step'
            : "Enter your email and we'll send you a reset link"}
        </p>
      </motion.div>

      {submitted ? (
        <motion.div variants={formItemVariants} className="space-y-5">
          <div
            role="status"
            aria-live="polite"
            className="flex items-start gap-3 rounded-md border border-emerald-500/20 bg-emerald-500/[0.06] p-4"
          >
            <CheckCircle
              className="h-5 w-5 text-emerald-600 dark:text-emerald-400 shrink-0 mt-0.5"
              weight="fill"
            />
            <div className="space-y-2">
              <p
                className="text-[#3f5147] dark:text-[#a7d8bf]"
                style={{ fontSize: '13.5px', lineHeight: 1.5, letterSpacing: '-0.13px' }}
              >
                If an account exists for{' '}
                <span style={{ fontWeight: 510 }}>{sentTo}</span>, a password reset
                link is on its way. The link expires in 1 hour.
              </p>
              <p
                className="text-[#5f6166] dark:text-[#8a8f98]"
                style={{ fontSize: '12.5px', lineHeight: 1.5, letterSpacing: '-0.1px' }}
              >
                Delivery can take a minute. If it hasn't arrived, check your spam
                or junk folder before sending another.
              </p>
            </div>
          </div>

          {/* A confirmation with only a "go back" is a dead end (§24) — give the
              user whose mail never lands something to actually do. */}
          <button
            type="button"
            onClick={handleResend}
            disabled={cooldown > 0 || isResending}
            aria-busy={isResending}
            className="w-full h-10 rounded-md bg-white border border-black/[0.08] text-[#0f1011] hover:bg-[#f5f5f5] hover:border-black/[0.14] dark:bg-white/[0.02] dark:border-white/[0.08] dark:text-[#e2e4e7] dark:hover:bg-white/[0.04] dark:hover:border-white/[0.12] inline-flex items-center justify-center gap-2 disabled:opacity-60 disabled:pointer-events-none focus:outline-none focus:ring-2 focus:ring-[#5e6ad2]/30 focus:ring-offset-2 focus:ring-offset-white dark:focus:ring-offset-[#0f1011] transition-colors duration-150"
            style={{ fontSize: '14px', fontWeight: 510, letterSpacing: '-0.13px' }}
          >
            {isResending ? (
              <>
                <DotLoader size="sm" announce={false} />
                <span>Sending</span>
              </>
            ) : (
              <>
                <ArrowClockwise className="h-4 w-4" weight="bold" />
                {cooldown > 0
                  ? `Send again in ${cooldown}s`
                  : "Didn't get it? Send again"}
              </>
            )}
          </button>

          <button
            type="button"
            onClick={() => navigate('/login')}
            className="w-full inline-flex items-center justify-center gap-1.5 text-[#5f6166] hover:text-[#0f1011] dark:text-[#8a8f98] dark:hover:text-[#d0d6e0] transition-colors"
            style={{ fontSize: '13px', fontWeight: 510, letterSpacing: '-0.13px' }}
          >
            <ArrowLeft className="h-3.5 w-3.5" weight="bold" />
            Back to sign in
          </button>
        </motion.div>
      ) : (
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
          {/* Email */}
          <motion.div variants={formItemVariants}>
            <motion.div
              className="relative group"
              variants={shakeVariants}
              animate={errors.email ? 'error' : 'idle'}
            >
              <Envelope
                className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#7a7d83] dark:text-[#62666d] group-focus-within:text-[#5e6ad2] dark:group-focus-within:text-[#7170ff] transition-colors pointer-events-none z-10"
                weight="regular"
              />
              <input
                id="email"
                type="email"
                autoComplete="email"
                placeholder="Email address"
                aria-label="Email address"
                aria-invalid={errors.email ? true : undefined}
                aria-describedby={errors.email ? 'email-error' : undefined}
                autoFocus
                style={inputStyle}
                className={`${inputClass} ${errors.email ? errorBorderClass : ''}`}
                {...register('email')}
              />
            </motion.div>
            {errors.email && (
              <motion.p
                id="email-error"
                role="alert"
                className="mt-1.5 text-red-600 dark:text-red-400"
                style={{ fontSize: '13px', letterSpacing: '-0.13px', fontWeight: 400 }}
                initial={{ opacity: 0, y: -4 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.2, ease: easeOutExpo }}
              >
                {errors.email.message}
              </motion.p>
            )}
          </motion.div>

          {/* Submit */}
          <motion.button
            type="submit"
            disabled={isLoading}
            aria-busy={isLoading}
            className="w-full h-10 rounded-md bg-[#5e6ad2] text-white inline-flex items-center justify-center gap-2 disabled:opacity-60 disabled:pointer-events-none focus:outline-none focus:ring-2 focus:ring-[#5e6ad2]/40 focus:ring-offset-2 focus:ring-offset-white dark:focus:ring-offset-[#0f1011]"
            style={{ fontSize: '14px', fontWeight: 510, letterSpacing: '-0.13px' }}
            variants={formItemVariants}
            whileHover={{ backgroundColor: '#7170ff', transition: { duration: 0.15 } }}
            whileTap={{ scale: 0.97, backgroundColor: '#5058c0' }}
          >
            {isLoading ? (
              <>
                {/* The button announces its own busy state — §55: don't double-speak. */}
                <DotLoader size="sm" announce={false} />
                <span>Sending</span>
              </>
            ) : (
              <motion.span className="inline-flex items-center gap-2" whileHover="hover">
                Send reset link
                <motion.span
                  variants={{ hover: { x: 3 } }}
                  transition={{ duration: 0.2, ease: easeOutExpo }}
                  className="inline-flex"
                >
                  <ArrowRight className="h-4 w-4" weight="bold" />
                </motion.span>
              </motion.span>
            )}
          </motion.button>

          {/* Back to sign in */}
          <motion.button
            type="button"
            onClick={() => navigate('/login')}
            className="w-full inline-flex items-center justify-center gap-1.5 text-[#5f6166] hover:text-[#0f1011] dark:text-[#8a8f98] dark:hover:text-[#d0d6e0] transition-colors"
            style={{ fontSize: '13px', fontWeight: 510, letterSpacing: '-0.13px' }}
            variants={formItemVariants}
          >
            <ArrowLeft className="h-3.5 w-3.5" weight="bold" />
            Back to sign in
          </motion.button>
        </form>
      )}
    </motion.div>
  )
}

export default ForgotPasswordPage
